import React, { useState, useEffect } from 'react';

const TeacherList = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    department: 'All',
    designation: 'All'
  });

  useEffect(() => {
    // Mock data for teachers
    const mockTeachers = [
      {
        id: 1,
        name: 'John Doe',
        employeeId: 'EMP001',
        designation: 'Professor',
        department: 'Computer Science',
        email: 'john.doe@example.com',
        phone: '+1234567890',
        qualification: 'PhD in Computer Science',
        experience: 10,
        joiningDate: '2013-08-15',
        salary: 75000
      },
      {
        id: 2,
        name: 'Jane Smith',
        employeeId: 'EMP002',
        designation: 'Assistant Professor',
        department: 'Mathematics',
        email: 'jane.smith@example.com',
        phone: '+1234567891',
        qualification: 'MSc Mathematics',
        experience: 5,
        joiningDate: '2018-06-10',
        salary: 55000
      },
      {
        id: 3,
        name: 'Robert Johnson',
        employeeId: 'EMP003',
        designation: 'Lecturer',
        department: 'Physics',
        email: 'robert.johnson@example.com',
        phone: '+1234567892',
        qualification: 'MSc Physics',
        experience: 3,
        joiningDate: '2020-07-20',
        salary: 45000
      }
    ];
    
    setTeachers(mockTeachers);
    setLoading(false);
  }, []);

  const filteredTeachers = teachers.filter(teacher => {
    return (
      (filters.department === 'All' || teacher.department === filters.department) &&
      (filters.designation === 'All' || teacher.designation === filters.designation)
    );
  });

  const departments = ['All', 'Computer Science', 'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'History', 'Geography'];
  const designations = ['All', 'Professor', 'Assistant Professor', 'Lecturer', 'Instructor', 'Head of Department'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Manage Teachers</h1>
          <a 
            href="/admin/teachers/add" 
            className="bg-primary text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Add New Teacher
          </a>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 mb-2">Department</label>
              <select
                className="w-full p-2 border rounded"
                value={filters.department}
                onChange={(e) => setFilters({...filters, department: e.target.value})}
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Designation</label>
              <select
                className="w-full p-2 border rounded"
                value={filters.designation}
                onChange={(e) => setFilters({...filters, designation: e.target.value})}
              >
                {designations.map(designation => (
                  <option key={designation} value={designation}>{designation}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button className="w-full bg-primary text-white px-4 py-2 rounded hover:bg-blue-700">
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* Teachers Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Experience</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joining Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTeachers.map((teacher) => (
                  <tr key={teacher.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{teacher.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teacher.employeeId}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teacher.designation}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teacher.department}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teacher.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teacher.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teacher.experience} years</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teacher.joiningDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <a href={`/admin/teachers/edit/${teacher.id}`} className="text-blue-600 hover:text-blue-900">Edit</a>
                      <a href={`/admin/teachers/view/${teacher.id}`} className="text-gray-600 hover:text-gray-900 ml-2">View</a>
                      <button className="text-red-600 hover:text-red-900 ml-2">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherList;